/* eslint-disable no-unused-vars */
import {
  forwardRef,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Source } from "@types";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import useManageVideo from "@modules/player/hooks/useMangeVideo";
import { getVideoType } from "@utils/downloadLink";
import { isInDevEnv } from "@utils/helpers";
import Controls from "@modules/player/controls";
import {
  EMBED_PLAYER_FRAME_CLASSNAME,
  getEmbedPlayerFrameStyle,
} from "@features/video-course/components/embeded-skeleton";
import VideoPoster from "@features/video-course/components/VideoPoster";

const RightClickMenu = lazy(() => import("@modules/player/right-click-menu"));

export type Props = {
  sources: Source[];
  poster: string;
  timeUpdateHandler?: (
    time: number,
    duration: number,
    seek: boolean
  ) => Promise<void>;
  onEnd?: (time: number, duration: number) => Promise<void>;
  autoPlayOnMount?: boolean;
  className?: string;
  id?: number;
  subtitleSrc?: string;
  startFrom?: number;
  onPlay?: () => void;
  withControls?: boolean;
  defaultMuted?: boolean;
  maxHeight?: number;
};

export type PlayerElement = {
  togglePlayHandler: () => void;
  pausePlayer: () => void;
};

const isProbablyIOS = () => {
  if (typeof window === "undefined") return false;

  const ua = window.navigator.userAgent;
  const platform = window.navigator.platform;

  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  );
};

const EmbedFixedHeightPlayer = forwardRef<PlayerElement, Props>(
  (
    {
      sources,
      poster,
      timeUpdateHandler,
      onEnd,
      className = "",
      id,
      subtitleSrc,
      startFrom,
      onPlay,
      autoPlayOnMount,
      withControls = true,
      defaultMuted,
      maxHeight,
    },
    ref
  ) => {
    const { isPlaying, ...videoProps } = useManageVideo({
      ref,
      sources,
      theaterMode: false,
      subtitle: subtitleSrc,
      startFrom,
      curVideoId: id,
      timeUpdateHandler,
      onEnd,
      autoPlayOnSrcChange: false,
      onPlay,
      defaultMuted,
      autoPlayOnMount,
    });

    const {
      videoContainerRef,
      debouncedAnimatePlay,
      showControls,
      hideControls,
      mouseMoveHandler,
      videoRef,
      settings,
      browser,
      playHandler,
      pauseHandler,
      loadMetaDataHandler,
      videoTimeUpdateHandler,
      videoVolumeChangeHandler,
      replayAfterSeek,
      videoEndedHandler,
      canPlayHandler,
      setIsLoading,
      position,
      menuRef,
      controlsRef,
      showVideoControls,
      videoSources,
      handleClick,
      currentText,
      calcHeight,
    } = videoProps;

    const [isIPhone, setIsIPhone] = useState(isProbablyIOS());

    const autoplayBlockedRef = useRef(false);
    const wantsAutoplay = Boolean(autoPlayOnMount);

    useEffect(() => {
      setIsIPhone(isProbablyIOS());
    }, []);

    useEffect(() => {
      if (!wantsAutoplay) return;

      const video = videoRef.current;
      if (!video) return;

      let cancelled = false;

      const attempt = () => {
        const node = videoRef.current;
        if (!node || cancelled || autoplayBlockedRef.current || !node.paused) {
          return;
        }

        if (defaultMuted) node.muted = true;

        node.play().catch((error: unknown) => {
          if (cancelled || !node.paused) return;

          const name = error instanceof DOMException ? error.name : "";
          if (name === "NotAllowedError" && !defaultMuted)
            autoplayBlockedRef.current = true;
        });
      };

      attempt();
      video.addEventListener("loadeddata", attempt);
      video.addEventListener("canplay", attempt);

      return () => {
        cancelled = true;
        video.removeEventListener("loadeddata", attempt);
        video.removeEventListener("canplay", attempt);
      };
    }, [defaultMuted, sources, videoRef, wantsAutoplay]);

    const activeVideo = useMemo(() => {
      return (
        (sources || []).find(
          (src) => src.label === videoProps?.settings?.quality
        ) || sources[0]
      );
    }, [sources, videoProps?.settings?.quality]);

    const shouldRenderCustomControls = Boolean(!isIPhone && withControls);

    const sourceType = useMemo(() => {
      if (!activeVideo?.url) return undefined;

      return activeVideo.type || getVideoType(activeVideo.url);
    }, [activeVideo]);

    return (
      <div
        className={twMerge(EMBED_PLAYER_FRAME_CLASSNAME, className)}
        style={getEmbedPlayerFrameStyle(maxHeight)}
      >
        <div className="absolute inset-0">
          <div
            ref={videoContainerRef}
            className={classNames(
              "courseItemPageSelector nplay-video-container relative w-full h-full outline-none focus:outline-none border-none !border-0",
              debouncedAnimatePlay ? "cursor-default" : ""
            )}
            onMouseEnter={showControls}
            onMouseLeave={hideControls}
            onMouseMove={mouseMoveHandler}
          >
            <video
              ref={videoRef}
              className={twMerge(
                "relative nplay-video w-full h-full top-0 outline-none align-middle block focus:outline-none",
                settings.fullScreen
                  ? "object-contain top-1/2 -translate-y-1/2 sm:translate-y-0 sm:top-0 sm:w-full sm:h-full subtitle"
                  : "object-contain",
                browser.isFirefox ? "firefox" : "noFireFox"
              )}
              preload={wantsAutoplay ? "auto" : "metadata"}
              poster={poster}
              muted={settings.isMuted}
              autoPlay={wantsAutoplay && defaultMuted}
              playsInline
              controls={Boolean(isIPhone) || showVideoControls || !withControls}
              onClick={handleClick}
              onPlay={playHandler}
              onPause={pauseHandler}
              onLoadedMetadata={loadMetaDataHandler}
              onTimeUpdate={videoTimeUpdateHandler}
              onVolumeChange={videoVolumeChangeHandler}
              onSeeked={replayAfterSeek}
              onEnded={videoEndedHandler}
              onCanPlay={canPlayHandler}
              onWaiting={() => setIsLoading(true)}
              onPlaying={() => setIsLoading(false)}
              crossOrigin={isInDevEnv() ? undefined : "anonymous"}
            >
              {activeVideo?.url ? (
                <source
                  key={activeVideo.url}
                  src={activeVideo.url}
                  type={sourceType}
                />
              ) : null}
            </video>

            {currentText && settings.subtitleOn ? (
              <span
                className={twMerge(
                  "customSubtitle relative z-[3]",
                  settings.fullScreen
                    ? "text-sm md:text-xl"
                    : "text-xs md:text-lg"
                )}
                style={{
                  transform: `translate(-50%,-${calcHeight()}px)`,
                }}
              >
                {currentText}
              </span>
            ) : null}

            {!isIPhone && typeof isPlaying === "undefined" && poster ? (
              <VideoPoster poster={poster} />
            ) : null}

            {shouldRenderCustomControls ? (
              <Controls
                {...videoProps}
                animatePlay={debouncedAnimatePlay}
                ref={controlsRef}
                videoNode={videoRef.current}
                sources={videoSources}
                isPlaying={Boolean(isPlaying)}
                showTheater={false}
                isControlsVisible={videoProps.isControlsVisible}
                showNext={false}
              />
            ) : null}
          </div>

          {position.show ? (
            <Suspense fallback={null}>
              <RightClickMenu
                left={position.x || 0}
                top={position.y || 0}
                isMuted={settings.isMuted}
                speedRateChangeHandler={videoProps.speedRateChangeHandler}
                toggleMuteHandler={videoProps.toggleMuteHandler}
                togglePlayByClickHandler={videoProps.togglePlayByClickHandler}
                togglePipHandler={videoProps.togglePipHandler}
                pipEnable={settings.pipEnable}
                fullScreenEnable={settings.fullScreenEnable}
                toggleFullscreenHandler={videoProps.toggleFullscreenHandler}
                targetRef={menuRef}
                startPlayer={videoProps.startPlayer}
                currentSpeed={settings.speedRate}
                isPlaying={Boolean(isPlaying)}
                closeMenu={videoProps.closeMenu}
              />
            </Suspense>
          ) : null}
        </div>
      </div>
    );
  }
);

EmbedFixedHeightPlayer.displayName = "EmbedFixedHeightPlayer";

export default EmbedFixedHeightPlayer;
